import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MerchantPaymentService } from 'src/app/services/merchant-payment.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-merchant-payment',
  templateUrl: './merchant-payment.component.html',
  styleUrls: ['./merchant-payment.component.scss']
})
export class MerchantPaymentComponent implements OnInit {
  merchantId = Number(localStorage.getItem('merchant_id'));
  userId = Number(localStorage.getItem('user_id'));
  userType = Number(localStorage.getItem('user_type'));
  curentLang = localStorage.getItem('curentLang') || 'ar';

  cards: any[] = [];
  transactions: any[] = [];
  loadingCards = false;
  loadingTx = false;
  paymentUrl: SafeResourceUrl | null = null;
  invoicePdfUrl: SafeResourceUrl | null = null;
  showInvoicePanel = false;
  loadingInvoicePdf = false;
  invoicePdfRawUrl: string | null = null;
  /** Same-tab full page PayPlus registration (replaces cards list). */
  showCardRegistrationPanel = false;

  constructor(
    private paymentService: MerchantPaymentService,
    private spinner: NgxSpinnerService,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {}

  goBack(): void {
    this.router.navigate(['/dashboard/dashboard']);
  }

  ngOnInit(): void {
    if (this.userType !== 100 || !this.merchantId) {
      return;
    }
    this.loadCards();
    this.loadTransactions();
  }

  loadCards() {
    this.loadingCards = true;
    this.paymentService.getCards(this.merchantId).subscribe({
      next: res => { this.cards = res || []; this.loadingCards = false; },
      error: () => { this.loadingCards = false; }
    });
  }

  loadTransactions() {
    this.loadingTx = true;
    this.paymentService.getTransactions(this.merchantId).subscribe({
      next: res => { this.transactions = res || []; this.loadingTx = false; },
      error: () => { this.loadingTx = false; }
    });
  }

  registerCard() {
    if (!this.merchantId) { return; }
    this.spinner.show();
    this.paymentService.registerCard(this.merchantId, this.userId, this.curentLang).subscribe({
      next: res => {
        this.spinner.hide();
        if (res?.paymentUrl) {
          this.paymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.paymentUrl);
          this.showCardRegistrationPanel = true;
        }
      },
      error: () => this.spinner.hide()
    });
  }

  exitCardRegistration(): void {
    this.showCardRegistrationPanel = false;
    this.paymentUrl = null;
    this.loadCards();
  }

  activate(card: any) {
    this.paymentService.activateCard(this.merchantId, card.id).subscribe({
      next: () => { card.isActive = true; },
    });
  }

  deactivate(card: any) {
    this.paymentService.deactivateCard(this.merchantId, card.id).subscribe({
      next: () => { card.isActive = false; },
    });
  }

  delete(card: any) {
    const title = this.curentLang === 'en' ? 'Delete this card?' : (this.curentLang === 'he' ? 'למחוק כרטיס זה?' : 'هل تريد حذف هذا الكارت؟');
    const confirm = this.curentLang === 'en' ? 'Delete' : (this.curentLang === 'he' ? 'מחק' : 'حذف');
    const cancel = this.curentLang === 'en' ? 'Cancel' : (this.curentLang === 'he' ? 'ביטול' : 'إلغاء');
    Swal.fire({ icon: 'warning', title, showCancelButton: true, confirmButtonText: confirm, cancelButtonText: cancel })
      .then(r => {
        if (!r.isConfirmed) return;
        this.paymentService.deleteCard(this.merchantId, card.id).subscribe({
          next: () => { this.cards = this.cards.filter(c => c.id !== card.id); }
        });
      });
  }

  /**
   * PayPlus brandId mapping (confirmed + BIN-based fallback):
   * 1 = Visa, 2 = Mastercard, 3 = Amex, 4 = Diners,
   * 5 = Discover, 6 = JCB, 7 = Maestro, 8 = Visa Electron (BIN 4xxxxx)
   */
  brandIcon(brandId: number, cardBin?: string): string {
    const map: Record<number, string> = {
      1: 'fa-cc-visa',
      2: 'fa-cc-mastercard',
      3: 'fa-cc-amex',
      4: 'fa-cc-diners-club',
      5: 'fa-cc-discover',
      6: 'fa-cc-jcb',
      7: 'fa-cc-mastercard',  // Maestro → Mastercard family
      8: 'fa-cc-visa',        // Visa Electron
    };
    if (map[brandId]) return map[brandId];
    // BIN fallback
    if (cardBin) {
      if (cardBin.startsWith('4')) return 'fa-cc-visa';
      if (/^5[1-5]/.test(cardBin) || /^2[2-7]/.test(cardBin)) return 'fa-cc-mastercard';
      if (cardBin.startsWith('3')) return 'fa-cc-amex';
    }
    return 'fa-credit-card';
  }

  brandName(brandId: number, cardBin?: string): string {
    const map: Record<number, string> = {
      1: 'VISA',
      2: 'MASTERCARD',
      3: 'AMEX',
      4: 'DINERS',
      5: 'DISCOVER',
      6: 'JCB',
      7: 'MAESTRO',
      8: 'VISA ELECTRON',
    };
    if (map[brandId]) return map[brandId];
    if (cardBin) {
      if (cardBin.startsWith('4')) return 'VISA';
      if (/^5[1-5]/.test(cardBin) || /^2[2-7]/.test(cardBin)) return 'MASTERCARD';
      if (cardBin.startsWith('3')) return 'AMEX';
    }
    return '';
  }

  /** لون خلفية الكارت */
  cardGradient(brandId: number, cardBin?: string): string {
    const gradients: Record<number, string> = {
      1: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',  // Visa – أزرق داكن
      2: 'linear-gradient(135deg, #e52d27 0%, #b31217 100%)',               // Mastercard – أحمر
      3: 'linear-gradient(135deg, #2d6a4f 0%, #52b788 100%)',               // Amex – أخضر
      4: 'linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%)',               // Diners – رمادي داكن
      5: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',               // Discover – ذهبي
      6: 'linear-gradient(135deg, #005f73 0%, #0a9396 100%)',               // JCB – أزرق مخضر
      7: 'linear-gradient(135deg, #e52d27 0%, #b31217 100%)',               // Maestro – Mastercard
      8: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)', // Visa Electron
    };
    if (gradients[brandId]) return gradients[brandId];
    if (cardBin) {
      if (cardBin.startsWith('4')) return gradients[1];
      if (/^5[1-5]/.test(cardBin) || /^2[2-7]/.test(cardBin)) return gradients[2];
    }
    return 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  txStatusBadge(status: string): string {
    if (!status) return 'bg-secondary';
    switch (status.toUpperCase()) {
      case 'SUCCESS': return 'bg-success';
      case 'FAILED': return 'bg-danger';
      case 'PENDING': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  }

  private resolveInvoiceId(tx: any): number | null {
    const possibleId =
      tx?.issuedInvoiceId ??
      tx?.invoiceId ??
      tx?.invoice?.id ??
      tx?.issuedInvoice?.id;
    const invoiceId = Number(possibleId);
    return Number.isFinite(invoiceId) && invoiceId > 0 ? invoiceId : null;
  }

  showInvoice(tx: any): void {
    const invoiceId = this.resolveInvoiceId(tx);
    if (!invoiceId) {
      Swal.fire({
        icon: 'warning',
        title: this.curentLang === 'en' ? 'Invoice is not available' : (this.curentLang === 'he' ? 'החשבונית אינה זמינה' : 'الفاتورة غير متاحة'),
      });
      return;
    }

    this.loadingInvoicePdf = true;
    this.paymentService.getInvoicePdfLink(invoiceId, this.curentLang).subscribe({
      next: (res) => {
        this.loadingInvoicePdf = false;
        if (!res?.pdf_link) return;
        this.invoicePdfRawUrl = res.pdf_link;
        this.invoicePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.pdf_link);
        this.showInvoicePanel = true;
      },
      error: () => {
        this.loadingInvoicePdf = false;
        Swal.fire({
          icon: 'error',
          title: this.curentLang === 'en' ? 'Could not load invoice PDF' : (this.curentLang === 'he' ? 'לא ניתן לטעון את קובץ ה־PDF של החשבונית' : 'تعذر تحميل ملف PDF الخاص بالفاتورة'),
        });
      }
    });
  }

  closeInvoicePanel(): void {
    this.showInvoicePanel = false;
    this.invoicePdfUrl = null;
    this.invoicePdfRawUrl = null;
  }

  openInvoiceInNewTab(): void {
    if (!this.invoicePdfRawUrl) return;
    window.open(this.invoicePdfRawUrl, '_blank', 'noopener,noreferrer');
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.showInvoicePanel) {
      this.closeInvoicePanel();
    }
  }

}

